/**
 * 航段费用结算导入模态框组件
 * 支持Excel上传、预览和结算系统集成
 * 
 * 新增功能：
 * 1. 智能模板下载（后端动态生成）
 * 2. 两阶段导入（校验 -> 确认导入）
 * 3. 失败记录导出
 */

import React, { useState } from "react";
import { Modal, Space, Button, Typography, Upload, Spin, Table, Card, Alert, message, Tag, Row, Col, Statistic, Divider, Radio } from "antd";
import { InboxOutlined, FileExcelOutlined, DownloadOutlined, CheckCircleOutlined, SyncOutlined, ExclamationCircleOutlined, WarningOutlined } from "@ant-design/icons";
import FrontendImportService from "../../services/frontendImportService";
import { settlementApi } from "../../api/settlementApi";
import { exportFailedRecordsToExcel } from "../../utils/excelUtils";

const { Text, Title } = Typography;
const { Dragger } = Upload;

/**
 * 航段费用导入模态框组件
 * @param {Object} props - 组件属性
 * @param {boolean} props.isOpen - 是否显示对话框
 * @param {function} props.onClose - 关闭对话框回调
 * @param {function} props.onSuccess - 成功回调
 * @param {string} props.importType - 导入类型 (目前只支持 leg_cost_import)
 */
const FrontendImportModal = ({ isOpen, onClose, onSuccess, importType = "leg_cost_import" }) => {
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [precheckResult, setPrecheckResult] = useState(null);
    const [importMode, setImportMode] = useState("overwrite");
    const [precheckLoading, setPrecheckLoading] = useState(false);
    // S2 P0 (2026-05): 解析失败但原件已存档时,记录 data_source_id 用于"打开原件"按钮
    const [archivedDataSourceId, setArchivedDataSourceId] = useState(null);
    const [archivedRawFilePath, setArchivedRawFilePath] = useState(null);

    // 初始化服务
    const importService = new FrontendImportService();

    const resetState = () => {
        setFileList([]);
        setValidationResult(null);
        setErrorMessage(null);
        setLoading(false);
        setUploadedFile(null);
        setConfirmModalVisible(false);
        setPrecheckResult(null);
        setImportMode("overwrite");
        setPrecheckLoading(false);
        setArchivedDataSourceId(null);
        setArchivedRawFilePath(null);
    };

    // 处理对话框关闭
    const handleClose = () => {
        resetState();
        onClose();
    };

    // 处理文件变更
    const handleFileChange = ({ fileList }) => {
        setFileList(fileList.slice(-1)); // 只保留最后一个文件
        setValidationResult(null);
        setErrorMessage(null);
        
        // 保存原始文件对象，用于后续的重复检测
        if (fileList.length > 0) {
            const file = fileList[0].originFileObj || fileList[0];
            setUploadedFile(file);
        } else {
            setUploadedFile(null);
        }
    };

    // 下载模板（后端动态生成）
    const handleDownloadTemplate = async () => {
        try {
            await importService.downloadTemplate();
            message.success("模板下载成功");
        } catch (error) {
            console.error("模板下载失败:", error);
            message.error("模板下载失败");
        }
    };

    // 校验文件（第一阶段）
    const handleValidate = async () => {
        if (fileList.length === 0) {
            message.error("请选择要导入的Excel文件");
            return;
        }

        const file = fileList[0].originFileObj || fileList[0];

        if (!file) {
            message.error("文件对象无效，请重新选择文件");
            return;
        }

        // 确保file是File对象
        if (!(file instanceof File)) {
            message.error("文件对象类型错误，请重新选择文件");
            console.error("❌ 文件不是File对象:", file);
            return;
        }

        // 验证文件类型
        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'application/vnd.ms-excel.sheet.macroEnabled.12'
        ];

        if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
            message.error("请选择Excel文件 (.xlsx 或 .xls)");
            return;
        }

        setLoading(true);
        setErrorMessage(null);

        try {
            const response = await settlementApi.legCost.validateLegCostFile(file);
            
            if (response.data.success) {
                setValidationResult(response.data.data);
                message.success("文件校验完成");
            } else {
                setErrorMessage("校验失败");
                message.error("文件校验失败");
            }
        } catch (error) {
            console.error("校验失败:", error);
            const errorMsg = error.response?.data?.detail || error.message || "校验失败";
            setErrorMessage(errorMsg);
            message.error("文件校验失败：" + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // 点击"确认导入"按钮：先预检，再弹窗让用户选择模式
    const handleConfirm = async () => {
        if (!validationResult?.valid_data?.length) {
            message.error("没有有效记录可导入");
            return;
        }
        if (!uploadedFile) {
            message.error("文件对象丢失，请重新上传文件");
            return;
        }

        setPrecheckLoading(true);
        try {
            const res = await settlementApi.legCost.precheckLegCostImport(validationResult.valid_data);
            const data = res.data?.data || res.data;
            setPrecheckResult(data);
            setImportMode("overwrite");
            setConfirmModalVisible(true);
        } catch (error) {
            console.error("预检失败:", error);
            message.error("预检失败：" + (error.response?.data?.detail || error.message));
        } finally {
            setPrecheckLoading(false);
        }
    };

    // 二次确认弹窗中点击"执行导入"
    const handleExecuteImport = async () => {
        setConfirmModalVisible(false);
        setLoading(true);
        // 每次开始导入前清掉上次的原件档案信息(成功/失败都重新写)
        setArchivedDataSourceId(null);
        setArchivedRawFilePath(null);

        try {
            const response = await settlementApi.legCost.confirmLegCostImportWithDuplicateHandling(
                validationResult.valid_data,
                uploadedFile,
                importMode
            );

            if (response.data.success) {
                const summary = response.data.processing_summary || {};
                const totalProcessed = summary.total_processed || 0;
                const newRecords = summary.new_records || 0;
                const updatedRecords = summary.updated_records || 0;
                const accumulatedRecords = summary.accumulated_records || 0;

                // S2 P0: 记录原件档案信息,便于成功后续追溯("查看原件")
                if (response.data.raw_file_archive?.data_source_id) {
                    setArchivedDataSourceId(response.data.raw_file_archive.data_source_id);
                    setArchivedRawFilePath(response.data.raw_file_archive.raw_file_path);
                }

                let successMsg = `导入成功！共处理 ${totalProcessed} 条记录`;
                if (importMode === "accumulate" && accumulatedRecords > 0) {
                    successMsg += `（新增 ${newRecords} 条，叠加 ${accumulatedRecords} 条）`;
                } else if (updatedRecords > 0) {
                    successMsg += `（新增 ${newRecords} 条，覆盖 ${updatedRecords} 条）`;
                }

                message.success(successMsg);

                if (response.data.affected_batches?.length > 0) {
                    message.warning(`检测到 ${response.data.affected_batches.length} 个批次需要重新计算`, 5);
                }

                setTimeout(() => {
                    if (onSuccess) onSuccess();
                    handleClose();
                }, 1000);
            } else {
                message.error(response.data.message || "导入失败");
            }
        } catch (error) {
            console.error("导入失败:", error);

            // S2 P0 (2026-05): 后端的两类受控错误码 - 区分文案,场景 3 暴露"打开原件"
            const detail = error.response?.data?.detail;
            const errorCode = typeof detail === "object" ? detail?.error_code : null;

            if (errorCode === "RAW_FILE_UPLOAD_FAILED") {
                // 规格 S2 场景 2: 原件上传失败,本次作废,数据库零写入
                setErrorMessage("原件上传失败,本次作废,请稍后重试");
                message.error({
                    content: "原件上传失败,本次作废,请稍后重试(数据未入库)",
                    duration: 6,
                });
            } else if (errorCode === "PARSE_FAILED") {
                // 规格 S2 场景 3: 原件已存档但业务事务失败,可"打开原件"确认问题
                if (detail?.data_source_id) {
                    setArchivedDataSourceId(detail.data_source_id);
                    setArchivedRawFilePath(detail.raw_file_path || null);
                }
                setErrorMessage("原件已存档但解析/入库失败,可重试解析(无需重传)");
                message.error({
                    content: `原件已存档但解析/入库失败: ${detail?.message || "未知错误"}。可在"查看原件"中确认 Excel 格式问题`,
                    duration: 8,
                });
            } else {
                // 兜底:其它未受控错误
                const msg = typeof detail === "string" ? detail : detail?.message || error.message || "导入失败";
                message.error("导入失败：" + msg);
            }
        } finally {
            setLoading(false);
        }
    };

    // S2 P0: 在错误页/成功后打开原件(签名 URL)
    // TODO(T19): 接 S4 的 GET /api/v3/settlement/invoice-line-items/{id}/preview-url 后,
    // S4 T19 配套端点已就绪 — 走 /v3/settlement/invoice-data-sources/{id}/preview-url 拿签名 URL,
    // 在新页签打开。签名默认 5 分钟有效,前端不持久化 URL,避免被截图泄露。
    const handleOpenArchivedRawFile = async () => {
        if (!archivedDataSourceId) {
            message.warning("未找到原件档案 ID");
            return;
        }
        try {
            const res = await settlementApi.dataQuality.getInvoiceDataSourcePreviewUrl(
                archivedDataSourceId
            );
            const previewUrl = res.data?.url;
            if (!previewUrl) {
                message.error("生成原件签名 URL 失败");
                return;
            }
            window.open(previewUrl, "_blank", "noopener,noreferrer");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const errorCode = typeof detail === "object" ? detail?.error_code : undefined;
            if (errorCode === "RAW_FILE_NOT_ARCHIVED") {
                message.error(
                    "原件未归档 — 可能是历史数据,无法预览。请联系运营管理员从其它渠道补充。"
                );
            } else {
                message.error(
                    "打开原件失败: " +
                        (typeof detail === "string" ? detail : detail?.message || err.message)
                );
            }
        }
    };

    // 下载失败记录
    const handleDownloadFailedRecords = () => {
        if (!validationResult?.invalid_data?.length) {
            message.warning("没有失败记录可导出");
            return;
        }

        try {
            exportFailedRecordsToExcel(validationResult.invalid_data);
            message.success("失败记录已导出");
        } catch (error) {
            console.error("导出失败记录失败:", error);
            message.error("导出失败记录失败");
        }
    };

    // 渲染预览数据
    const renderPreview = () => {
        if (!validationResult) return null;

        const {
            total_count,
            valid_count,
            invalid_count,
            valid_data,
            invalid_data,
            duplicate_count = 0, // 后端注入:有效记录中与库内重复的条数(用来高亮)
        } = validationResult;

        return (
            <div style={{ marginTop: 24 }}>
                <Card
                    title={
                        <Space>
                            <CheckCircleOutlined style={{ color: "#52c41a" }} />
                            <span>解析结果</span>
                        </Space>
                    }
                    size="small"
                >
                    {/* 统计信息 — 4 列:总数 / 新增 / 已存在(将覆盖) / 无效 */}
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={6}>
                            <Statistic
                                title="总记录数"
                                value={total_count}
                                prefix={<FileExcelOutlined />}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="新增"
                                value={Math.max(valid_count - duplicate_count, 0)}
                                valueStyle={{ color: '#3f8600' }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="已存在(将覆盖)"
                                value={duplicate_count}
                                valueStyle={{ color: duplicate_count > 0 ? '#fa8c16' : undefined }}
                                prefix={<WarningOutlined />}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="无效记录"
                                value={invalid_count}
                                valueStyle={{ color: '#cf1322' }}
                                prefix={<ExclamationCircleOutlined />}
                            />
                        </Col>
                    </Row>

                    {/* 重复警告 — 校验阶段就告诉用户有多少条会被覆盖,
                        让"重复"在"确认导入"按钮按下之前就看得见 */}
                    {duplicate_count > 0 && (
                        <Alert
                            type="error"
                            showIcon
                            message={`⚠️ 检测到 ${duplicate_count} 条记录已存在于数据库,继续导入将覆盖现有金额`}
                            description="表格中橙色底色的行为已存在记录。点击「确认导入」后会再次弹窗,可选择「覆盖」或「叠加」。库内旧值会保留在变更历史中。"
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* 失败记录警告 */}
                    {invalid_count > 0 && (
                        <Alert
                            message={`发现 ${invalid_count} 条无效记录`}
                            description="请下载失败记录文件，修正后重新上传，或选择仅导入有效记录。"
                            type="warning"
                            showIcon
                            action={
                                <Button 
                                    size="small" 
                                    type="primary" 
                                    ghost 
                                    icon={<DownloadOutlined />}
                                    onClick={handleDownloadFailedRecords}
                                >
                                    下载失败记录
                                </Button>
                            }
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* 有效记录预览 */}
                    {valid_count > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Title level={5} style={{ marginBottom: 8 }}>
                                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                有效记录预览 ({valid_count} 条
                                {duplicate_count > 0 ? `,其中 ${duplicate_count} 条已存在` : ''})
                            </Title>
                            <Table
                                dataSource={valid_data}
                                onRow={(record) => ({
                                    // 重复行整行底色:浅橙(#fff7e6),让用户一眼分辨
                                    style: record.existing_in_db
                                        ? { background: '#fff7e6' }
                                        : undefined,
                                })}
                                columns={[
                                    {
                                        title: '状态',
                                        key: 'existing_in_db',
                                        width: 90,
                                        fixed: 'left',
                                        render: (_, record) =>
                                            record.existing_in_db ? (
                                                <Tag color="orange">已存在</Tag>
                                            ) : (
                                                <Tag color="green">新增</Tag>
                                            ),
                                    },
                                    { title: '航班号', dataIndex: 'flight_number', key: 'flight_number', width: 100 },
                                    {
                                        title: '航班日期',
                                        dataIndex: 'flight_date',
                                        key: 'flight_date',
                                        width: 120,
                                        render: (val) => {
                                            if (!val) return '-';
                                            if (val instanceof Date) {
                                                return val.toISOString().split('T')[0];
                                            }
                                            return String(val).split('T')[0];
                                        },
                                    },
                                    { title: '始发站', dataIndex: 'departure_airport', key: 'departure_airport', width: 80 },
                                    { title: '目的站', dataIndex: 'arrival_airport', key: 'arrival_airport', width: 80 },
                                    { title: '费用类型', dataIndex: 'cost_type', key: 'cost_type', width: 100 },
                                    { title: '供应商', dataIndex: 'supplier_code', key: 'supplier_code', width: 100 },
                                    { title: '币种', dataIndex: 'currency', key: 'currency', width: 60 },
                                    {
                                        title: '本次金额',
                                        dataIndex: 'amount',
                                        key: 'amount',
                                        width: 110,
                                        render: (val) => (val != null ? Number(val).toFixed(2) : '-'),
                                    },
                                    {
                                        // 库内原币金额 — 只对"已存在"的行有意义,
                                        // 让用户在校验阶段就能直接对比"我填的金额 vs 库里的金额"
                                        title: '库内金额',
                                        key: 'existing_original_amount',
                                        width: 110,
                                        render: (_, record) =>
                                            record.existing_in_db &&
                                            record.existing_original_amount != null ? (
                                                <Text strong style={{ color: '#fa8c16' }}>
                                                    {Number(record.existing_original_amount).toFixed(2)}
                                                </Text>
                                            ) : (
                                                <Text type="secondary">—</Text>
                                            ),
                                    },
                                    { title: '汇率', dataIndex: 'exchange_rate', key: 'exchange_rate', width: 80, render: (val) => val != null ? Number(val).toFixed(6) : '-' },
                                ]}
                                size="small"
                                rowKey={(record, index) => index}
                                pagination={{
                                    defaultPageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total) => `共 ${total} 条有效记录`,
                                    pageSizeOptions: ['10', '20', '50', '100']
                                }}
                                scroll={{ x: 'max-content' }}
                                bordered
                            />
                        </div>
                    )}

                    {/* 无效记录列表 */}
                    {invalid_count > 0 && (
                        <div>
                            <Divider />
                            <Title level={5} style={{ marginBottom: 8 }}>
                                <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                                无效记录列表 ({invalid_count} 条)
                            </Title>
                            <Table
                                dataSource={invalid_data} // 显示所有无效记录
                                columns={[
                                    { title: '行号', dataIndex: '_row_index', key: '_row_index', width: 60, fixed: 'left' },
                                    { title: '航班号', dataIndex: 'flight_number', key: 'flight_number', width: 100 },
                                    { 
                                        title: '航班日期', 
                                        dataIndex: 'flight_date', 
                                        key: 'flight_date', 
                                        width: 120,
                                        render: (val) => {
                                            if (!val) return '-';
                                            // 格式化日期为 YYYY-MM-DD
                                            if (val instanceof Date) {
                                                return val.toISOString().split('T')[0];
                                            }
                                            // 如果是字符串，提取日期部分
                                            return String(val).split('T')[0];
                                        }
                                    },
                                    { title: '始发站', dataIndex: 'departure_airport', key: 'departure_airport', width: 80 },
                                    { title: '目的站', dataIndex: 'arrival_airport', key: 'arrival_airport', width: 80 },
                                    { title: '时区', dataIndex: 'timezone', key: 'timezone', width: 80 },
                                    { title: '费用类型', dataIndex: 'cost_type', key: 'cost_type', width: 100 },
                                    { title: '供应商', dataIndex: 'supplier_code', key: 'supplier_code', width: 100 },
                                    { title: '币种', dataIndex: 'currency', key: 'currency', width: 60 },
                                    { title: '金额', dataIndex: 'amount', key: 'amount', width: 100, render: (val) => val != null ? Number(val).toFixed(2) : '-' },
                                    { title: '汇率', dataIndex: 'exchange_rate', key: 'exchange_rate', width: 80, render: (val) => val != null ? Number(val).toFixed(6) : '-' },
                                    { 
                                        title: '错误原因', 
                                        dataIndex: 'errorMessage', 
                                        key: 'errorMessage',
                                        width: 300,
                                        fixed: 'right',
                                        render: (text) => <Text type="danger">{text}</Text>
                                    },
                                ]}
                                size="small"
                                rowKey={(record, index) => index}
                                pagination={{
                                    defaultPageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total) => `共 ${total} 条无效记录`,
                                    pageSizeOptions: ['10', '20', '50', '100']
                                }}
                                scroll={{ x: 1400 }}
                                bordered
                            />
                        </div>
                    )}

                    {/* 操作按钮 */}
                    <div style={{ marginTop: 16, textAlign: "right" }}>
                        <Space>
                            <Button onClick={handleClose}>
                                取消
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleConfirm}
                                loading={loading || precheckLoading}
                                disabled={valid_count === 0}
                            >
                                确认导入 ({valid_count} 条有效记录)
                            </Button>
                        </Space>
                    </div>
                </Card>
            </div>
        );
    };

    return (
        <>
            <Modal
                title="Excel费用批量导入"
                open={isOpen}
                onCancel={handleClose}
                footer={null}
                width={1400}
                maskClosable={false}
                destroyOnHidden
                wrapClassName="import-modal-wide-wrapper"
                styles={{
                    body: {
                        padding: '20px 24px',
                        maxHeight: '75vh',
                        overflowY: 'auto'
                    }
                }}
            >
                <Spin spinning={loading} tip={validationResult ? "正在导入..." : "正在校验..."}>
                    {!loading && errorMessage && (
                        <Alert
                            message="操作失败"
                            description={errorMessage}
                            type="error"
                            showIcon
                            closable
                            style={{ marginBottom: 16 }}
                            onClose={() => setErrorMessage(null)}
                            action={
                                archivedDataSourceId ? (
                                    <Button size="small" type="primary" ghost onClick={handleOpenArchivedRawFile}>
                                        查看原件
                                    </Button>
                                ) : null
                            }
                        />
                    )}

                    {/* 文件上传区域 */}
                    <div>
                        <Dragger
                            name="file"
                            multiple={false}
                            fileList={fileList}
                            onChange={handleFileChange}
                            beforeUpload={() => false}
                            accept=".xlsx,.xls"
                            disabled={loading}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">点击或拖拽Excel文件到此区域进行上传</p>
                            <p className="ant-upload-hint">
                                支持 .xlsx 和 .xls 格式。请确保文件格式符合要求。
                            </p>
                        </Dragger>

                        {/* 下载模板按钮 */}
                        <div style={{ marginTop: 16 }}>
                            <Button
                                type="default"
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadTemplate}
                            >
                                下载Excel模板
                            </Button>
                        </div>

                        {/* 解析校验按钮 */}
                        <div style={{ marginTop: 16, textAlign: "right" }}>
                            <Button
                                type="primary"
                                icon={<SyncOutlined />}
                                onClick={handleValidate}
                                loading={loading}
                                disabled={fileList.length === 0}
                            >
                                解析校验
                            </Button>
                        </div>
                    </div>

                    {/* 预览区域（在上传区域下方） */}
                    {renderPreview()}
                </Spin>
            </Modal>

            {/* 二次确认弹窗:预检结果 + 覆盖/叠加选择 + 重复明细对比 */}
            <Modal
                title={
                    precheckResult && precheckResult.match_count > 0 ? (
                        <span style={{ color: '#cf1322' }}>
                            <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                            检测到 {precheckResult.match_count} 条重复 — 请确认处理方式
                        </span>
                    ) : (
                        '确认导入'
                    )
                }
                open={confirmModalVisible}
                onCancel={() => setConfirmModalVisible(false)}
                onOk={handleExecuteImport}
                okText={
                    precheckResult && precheckResult.match_count > 0
                        ? importMode === 'overwrite'
                            ? `执行覆盖(${precheckResult.match_count} 条)`
                            : `执行叠加(${precheckResult.match_count} 条)`
                        : '执行导入'
                }
                okButtonProps={
                    precheckResult && precheckResult.match_count > 0
                        ? { danger: true }
                        : {}
                }
                cancelText="再看看"
                width={precheckResult && precheckResult.match_count > 0 ? 980 : 520}
                maskClosable={false}
                destroyOnHidden
            >
                {precheckResult && (
                    <div>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={8}>
                                <Statistic
                                    title="总记录数"
                                    value={precheckResult.total_count}
                                    prefix={<FileExcelOutlined />}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="新增记录"
                                    value={precheckResult.new_count}
                                    valueStyle={{ color: '#3f8600' }}
                                    prefix={<CheckCircleOutlined />}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="重复记录(将被处理)"
                                    value={precheckResult.match_count}
                                    valueStyle={{ color: precheckResult.match_count > 0 ? '#cf1322' : undefined }}
                                    prefix={<WarningOutlined />}
                                />
                            </Col>
                        </Row>

                        {precheckResult.match_count > 0 ? (
                            <>
                                <Alert
                                    type="error"
                                    showIcon
                                    message={`下面 ${precheckResult.match_count} 条导入记录在系统里已有同组费用。继续导入会按你下面选择的方式处理。`}
                                    description="判定标准:同一航段(航班/起降地/日期/时区匹配出的内部航班) + 同一费用类型 + 同一供应商。叠加可以保留多笔；覆盖会将现有多笔合并为一笔。"
                                    style={{ marginBottom: 16 }}
                                />

                                {/* 重复明细 — 让用户在覆盖前直接对比"库内 vs 本次"两组金额 */}
                                <Table
                                    dataSource={precheckResult.match_details || []}
                                    rowKey={(r, idx) => r.existing_line_item_id || idx}
                                    size="small"
                                    bordered
                                    pagination={
                                        (precheckResult.match_details || []).length > 8
                                            ? { defaultPageSize: 8, showSizeChanger: false }
                                            : false
                                    }
                                    scroll={{ x: 'max-content', y: 320 }}
                                    style={{ marginBottom: 16 }}
                                    columns={[
                                        {
                                            title: 'Excel行',
                                            dataIndex: 'row_index',
                                            key: 'row_index',
                                            width: 70,
                                            fixed: 'left',
                                        },
                                        {
                                            title: '航班',
                                            key: 'flight',
                                            width: 180,
                                            render: (_, r) => (
                                                <Space size={4}>
                                                    <Text strong>{r.flight_number}</Text>
                                                    <Text type="secondary">
                                                        {r.departure_airport}→{r.arrival_airport}
                                                    </Text>
                                                    {r.flight_date && (
                                                        <Text type="secondary">{r.flight_date}</Text>
                                                    )}
                                                </Space>
                                            ),
                                        },
                                        { title: '费用', dataIndex: 'cost_type', key: 'cost_type', width: 90 },
                                        {
                                            title: '供应商',
                                            dataIndex: 'supplier_name',
                                            key: 'supplier_name',
                                            width: 140,
                                            render: (v) => v || <Text type="secondary">—</Text>,
                                        },
                                        {
                                            title: '库内笔数',
                                            dataIndex: 'existing_line_item_count',
                                            key: 'existing_line_item_count',
                                            width: 90,
                                            align: 'center',
                                            render: (v) => `${Number(v || 1)} 笔`,
                                        },
                                        {
                                            title: '库内合计',
                                            key: 'existing',
                                            width: 140,
                                            align: 'right',
                                            render: (_, r) => (
                                                <Text>
                                                    {Number(r.existing_original_amount || 0).toFixed(2)}{' '}
                                                    <Text type="secondary">{r.currency}</Text>
                                                </Text>
                                            ),
                                        },
                                        {
                                            title: '本次金额',
                                            key: 'new',
                                            width: 140,
                                            align: 'right',
                                            render: (_, r) => {
                                                const diff = Number(r.amount_diff_base || 0);
                                                const changed =
                                                    Math.abs(diff) >= 0.005 ||
                                                    Number(r.new_original_amount) !==
                                                        Number(r.existing_original_amount);
                                                return (
                                                    <Text
                                                        strong={changed}
                                                        style={changed ? { color: '#cf1322' } : undefined}
                                                    >
                                                        {Number(r.new_original_amount || 0).toFixed(2)}{' '}
                                                        <Text type="secondary">{r.currency}</Text>
                                                    </Text>
                                                );
                                            },
                                        },
                                        {
                                            title: '基准币差额',
                                            key: 'diff',
                                            width: 130,
                                            align: 'right',
                                            render: (_, r) => {
                                                const diff = Number(r.amount_diff_base || 0);
                                                if (Math.abs(diff) < 0.005) {
                                                    return <Text type="secondary">无变化</Text>;
                                                }
                                                const color = diff > 0 ? '#cf1322' : '#52c41a';
                                                const sign = diff > 0 ? '+' : '';
                                                return (
                                                    <Text strong style={{ color }}>
                                                        {sign}
                                                        {diff.toFixed(2)}
                                                    </Text>
                                                );
                                            },
                                        },
                                    ]}
                                />

                                <Radio.Group
                                    value={importMode}
                                    onChange={(e) => setImportMode(e.target.value)}
                                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                                >
                                    <Radio value="overwrite">
                                        <Text strong style={{ color: '#cf1322' }}>覆盖</Text>
                                        <Text type="secondary" style={{ marginLeft: 8 }}>
                                            每条导入记录都会用本次金额<Text strong>替换对应组合下的全部现有费用</Text>，并合并为一笔（旧记录保留历史，可追溯）
                                        </Text>
                                    </Radio>
                                    <Radio value="accumulate">
                                        <Text strong>叠加</Text>
                                        <Text type="secondary" style={{ marginLeft: 8 }}>
                                            将本次金额<Text strong>新增为一笔独立费用</Text>，现有费用保持不变（适用于分多笔结算）
                                        </Text>
                                    </Radio>
                                </Radio.Group>
                            </>
                        ) : (
                            <Alert
                                type="info"
                                showIcon
                                message="所有记录均为新增,将直接导入。"
                            />
                        )}
                    </div>
                )}
            </Modal>
        </>
    );
};

export default FrontendImportModal;
